export type SafeErrorKind =
  | 'not_found'
  | 'auth'
  | 'not_connected'
  | 'network'
  | 'server'
  | 'unknown';

export interface SafeUiError {
  kind: SafeErrorKind;
  title: string;
  description: string;
  retryable: boolean;
  recovery: string;
  requestId?: string;
}

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  requestId?: unknown;
  status?: unknown;
}

const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;

const readErrorLike = (error: unknown): ErrorLike => {
  if (!error || typeof error !== 'object') {
    return {};
  }
  return error as ErrorLike;
};

const inferKind = (error: ErrorLike): SafeErrorKind => {
  const status =
    typeof error.status === 'number' ? error.status : undefined;
  const code =
    typeof error.code === 'string' ? error.code.toLowerCase() : '';
  const message =
    typeof error.message === 'string'
      ? error.message.toLowerCase()
      : '';

  if (status === 401 || status === 403 || /auth|jwt|session/.test(code)) {
    return 'auth';
  }
  if (status === 404 || /not.?found|pgrst116/.test(code)) {
    return 'not_found';
  }
  if (
    /network|fetch|timeout|offline/.test(code) ||
    /network|failed to fetch|timeout|offline/.test(message)
  ) {
    return 'network';
  }
  if (
    (status !== undefined && status >= 500) ||
    /server|database|db_error/.test(code)
  ) {
    return 'server';
  }
  return 'unknown';
};

const SAFE_COPY: Record<SafeErrorKind, Omit<SafeUiError, 'kind' | 'requestId'>> = {
  not_found: {
    title: 'This item is unavailable',
    description:
      'It may have been removed, or you may no longer have access to it.',
    retryable: false,
    recovery: 'Return to the previous page and choose another item.',
  },
  auth: {
    title: 'Sign in required',
    description: 'Your session is missing or has expired.',
    retryable: false,
    recovery: 'Sign in again to continue.',
  },
  // Distinct from `auth` on purpose: the operator's own session is fine, an
  // upstream integration is not connected. Telling them to sign in again would
  // send them to fix the one thing that is working.
  not_connected: {
    title: 'Integration not connected',
    description: 'This feature needs an integration that is not connected right now.',
    retryable: false,
    recovery: 'Reconnect it in Settings. Everything else keeps working.',
  },
  network: {
    title: 'Connection problem',
    description: 'Tacit could not reach the service.',
    retryable: true,
    recovery: 'Check your connection and try again.',
  },
  server: {
    title: 'Tacit could not load this',
    description: 'The service is temporarily unavailable.',
    retryable: true,
    recovery: 'Try again in a moment.',
  },
  unknown: {
    title: 'Something went wrong',
    description: 'Tacit could not complete this request.',
    retryable: true,
    recovery: 'Try again. If the problem continues, contact support.',
  },
};

export const toSafeUiError = (
  error: unknown,
  requestedKind?: SafeErrorKind
): SafeUiError => {
  const errorLike = readErrorLike(error);
  const kind = requestedKind ?? inferKind(errorLike);
  const requestId =
    typeof errorLike.requestId === 'string' &&
    REQUEST_ID_PATTERN.test(errorLike.requestId)
      ? errorLike.requestId
      : undefined;

  return {
    kind,
    ...SAFE_COPY[kind],
    ...(requestId ? { requestId } : {}),
  };
};
